import { DetailsType, FormData } from 'types/application';
import { formatDateTime, formatCancellationDate } from 'utils/application.helpers';

import { 
    OTHER_SECT_TYPE as SECT_TYPE,
    OTHER_SECT_FILER as SECT_FILER,
    OTHER_SECT_APPLICANT as SECT_APPLICANT,
    OTHER_SECT_ADDRESS as SECT_ADDRESS,
    OTHER_SECT_COMPANY as SECT_COMPANY
} from 'common/application.const';
// ACA section IDs
import {
    SECT_COMPANY_DETAILS,
    SECT_OWNERSHIP,
    SECT_FINANCIAL_DETAILS,
    SECT_FUNDING_CREDIT_INFORMATION,
    SECT_BUSINESS_PLANS,
    SECT_PERSONNEL,
    SECT_POLICIES_OPERATIONS,
    SECT_INFORMATION_INSOLVENCY
} from 'common/application.const/submodules/aca/acaApplicationDetails';
import { payloadDeleteApplication, formDeleteApplication, formWithdrawApplication, payloadWithdrawApplication } from '../helpers';
import commonPayload from './common';
import { requestAttachmentListApplication } from 'store/api/application.api';

import { decryptId } from 'common/decrypt';

// Helper to get MIME type from file extension
const getMimeTypeFromExtension = (fileName: string): string => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'png': 'image/png',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'txt': 'text/plain'
    };
    return mimeTypes[ext || ''] || 'application/octet-stream';
};


type Props = {
    myInfoContent: any;
    uen?: string;
    id?: string;
};

const autoFillData = async ({ myInfoContent, uen, id }: Props) => {
    const decryptedID = await decryptId(id || '', 'decrypt');
    const decryptedUEN = await decryptId(uen || '', 'decrypt');
    const person = myInfoContent?.corppass || myInfoContent || {};
    const entity = myInfoContent?.['basic-profile'] || {};
    const corpUser = person?.['corppass-user'] || {};
    const address = myInfoContent?.address || person?.regadd || {};

    const isFin = ['F', 'G', 'M'].some((pre: string) => person?.uinfin?.value?.startsWith(pre));

    let idType = '';
    if (isFin) idType = 'FIN';
    // For ACA, ID type is not determined by nationality - user selects it manually

    let salutation = '';
    if (person.sex) {
        salutation = person.sex?.code === 'M' ? 'Mr.' : 'Ms.';
    }

    let corpUserID = corpUser['id-type']?.desc;
    if (corpUserID === 'NRIC') corpUserID = 'NRC';

    const generalForm = {
        id: 'general',
        formID: 'general',
        title: 'Applicant/Filer Details',
        hasError: true,
        values: {
            // Ensure applicant-type present
            [`${SECT_TYPE}-applicant-type`]: 'Applicant',
            [`${SECT_FILER}-id-type`]: corpUserID || idType,
            [`${SECT_FILER}-idno`]: decryptedID,
            [`${SECT_FILER}-salutaion`]: salutation,
            [`${SECT_FILER}-name`]: corpUser.name?.value || person?.name?.value,
            [`${SECT_FILER}-mobile-no-country-code`]: person?.mobileno?.areacode?.value || '65',
            [`${SECT_FILER}-mobile-no-main`]: person?.mobile?.value || person?.mobileno?.nbr?.value,
            [`${SECT_FILER}-email`]: person?.email?.value,
            [`${SECT_APPLICANT}-id-type`]: corpUserID || idType,
            [`${SECT_APPLICANT}-idno`]: decryptedID,
            [`${SECT_APPLICANT}-salutaion`]: salutation,
            [`${SECT_APPLICANT}-name`]: corpUser.name?.value || person?.name?.value,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: person?.mobileno?.areacode?.value || '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: person?.mobile?.value || person?.mobileno?.nbr?.value,
            [`${SECT_APPLICANT}-email`]: person?.email?.value,
            // Company fields for Corppass
            [`${SECT_COMPANY}-uen-no`]: decryptedUEN,
            [`${SECT_COMPANY}-entity-type`]: entity['entity-type']?.code || entity['entity-type']?.value || '',
            [`${SECT_COMPANY}-company`]: entity['entity-name']?.value,
            [`${SECT_COMPANY}-address-type`]: address.type || address.addresstype || 'SG',
            [`${SECT_COMPANY}-postal`]: address.postal?.value,
            [`${SECT_COMPANY}-block-house`]: address.block?.value,
            [`${SECT_COMPANY}-street`]: address.street?.value,
            [`${SECT_COMPANY}-floor`]: address.floor?.value,
            [`${SECT_COMPANY}-unit`]: address.unit?.value,
            [`${SECT_COMPANY}-building`]: address.building?.value,
            // Address fields for Singpass
            [`${SECT_ADDRESS}-address-type`]: address.type || address.addresstype || 'SG',
            [`${SECT_ADDRESS}-postal`]: address.postal?.value,
            [`${SECT_ADDRESS}-block-house`]: address.block?.value,
            [`${SECT_ADDRESS}-street`]: address.street?.value,
            [`${SECT_ADDRESS}-floor`]: address.floor?.value,
            [`${SECT_ADDRESS}-unit`]: address.unit?.value,
            [`${SECT_ADDRESS}-building`]: address.building?.value
        }
    };

    return generalForm;
};


const formatRetrieve = async (
    content: any,
    action?: string,
    dropdownOpts?: any,
    application?: string,
    caseTypeID?: string
) => {
    if (action === 'application-delete') return formDeleteApplication;
    if (action === 'application-withdraw') return formWithdrawApplication;
    
    // Handle download action - return application download files
    if (action === 'download') {
        return await commonPayload.getApplicationDownload(caseTypeID, content?.licenceName || 'Accreditation of Certification Authority');
    }
    
    // New contract: ignore legacy root Filer/Applicant nodes; rely on LICApplication subtree.
    const licApp = content.LICApplication || {};
    const licApplicant = licApp.applicant || {};
    const licFiler = licApp.filer || {};
    const licCompany = licApp.company || {};
    const licCompanyAddr = licCompany.registeredAddress || {};
    const profile = licApp.profile || {};
    const appliedAs = profile.applyAs === 'On behalf of applicant' ? 'Filer' : 'Applicant';
    const liceGeneral = licApp.general || {};

    // Addresses (flattened) already stored directly in applicant/filer.address
    // Fallback to company.registeredAddress if applicant.address is empty
    const applicantAddress = licApplicant.address || licCompany.registeredAddress || {};
    // (We currently do not store a separate filer address from form; left blank earlier.)

    // Determine transaction mode using the application parameter
    // Note: Backend caseType is always 'IMDA-IRIS-Work-LICApplication', but we pass specific types for routing
    const isAmendMode = application === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND';
    const isAmendView = application === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND-VIEW';
    const isCancelMode = application === 'IMDA-IRIS-Work-LICApplication-ACA-CANCEL';
    const isRenewMode = application === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW' || 
                        application === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY';
    const isRenewView = application === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-VIEW';
    const isRenewNoPayMode = application === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY';
    
    // Form IDs need suffix for both edit and view mode to match configuration
    const amendSuffix = (isAmendMode || isAmendView) ? '-amend' : '';
    const cancelSuffix = isCancelMode ? '-cancel' : '';
    // For NOPAY renew, use -renew-nopay suffix; for regular renew, use -renew
    const renewSuffix = isRenewNoPayMode ? '-renew-nopay' : (isRenewMode || isRenewView) ? '-renew' : '';
    const formSuffix = amendSuffix || cancelSuffix || renewSuffix;
    
    // Field keys only need suffix for AMEND mode, not VIEW mode or RENEW mode
    // VIEW mode uses ACA_APPLICATION_FIELDS which has normal section IDs
    // RENEW mode uses ACA_RENEW_APPLICATION_FIELDS which are the same as ACA_APPLICATION_FIELDS (no suffix)
    const fieldSuffix = isAmendMode ? '-amend' : '';

    const generalForm = {
        id: `general${formSuffix}`,
        formID: `general${formSuffix}`,
        title: 'Applicant/Filer Details',
        hasError: true,
        values: {
            [`${SECT_TYPE}-applicant-type`]: appliedAs,
            // Application number for cancel mode (read from pyID or applicationNumber)
            ...(isCancelMode && {
                [`general${formSuffix}-application-number`]: content?.applicationNumber || content?.pyID || ''
            }),
            // Filer (only if not Applicant)
            [`${SECT_FILER}-id-type`]: appliedAs === 'Filer' ? licFiler.idType : licApplicant.idType,
            [`${SECT_FILER}-idno`]: appliedAs === 'Filer' ? licFiler.idNumber : licApplicant.idNumber,
            [`${SECT_FILER}-salutaion`]: appliedAs === 'Filer' ? licFiler.salutation : licApplicant.salutation,
            [`${SECT_FILER}-name`]: appliedAs === 'Filer' ? licFiler.name : licApplicant.name,
            // Phone (ensure country code + main split; backend may only return unified contactNumber)
            [`${SECT_FILER}-mobile-no-country-code`]: '65',
            [`${SECT_FILER}-mobile-no-main`]: appliedAs === 'Filer' ? (licFiler.contactNumber || licFiler.mobileNumber || '') : (licApplicant.contactNumber || licApplicant.mobileNumber || ''),
            [`${SECT_FILER}-email`]: appliedAs === 'Filer' ? licFiler.email : licApplicant.email,
            // Applicant
            [`${SECT_APPLICANT}-id-type`]: licApplicant.idType,
            [`${SECT_APPLICANT}-idno`]: licApplicant.idNumber,
            [`${SECT_APPLICANT}-salutaion`]: licApplicant.salutation,
            [`${SECT_APPLICANT}-name`]: licApplicant.name,
            [`${SECT_APPLICANT}-uen-no`]: licApplicant.companyUEN || licCompany.uen,
            [`${SECT_APPLICANT}-company`]: licApplicant.companyName || licCompany.companyName,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: licApplicant.contactNumber || licApplicant.mobileNumber || '',
            [`${SECT_APPLICANT}-email`]: licApplicant.email,
            // Company details for Corppass
            [`${SECT_COMPANY}-uen-no`]: licApplicant.companyUEN || licCompany.uen,
            [`${SECT_COMPANY}-entity-type`]: licCompany.entityType || '',
            [`${SECT_COMPANY}-company`]: licApplicant.companyName || licCompany.companyName,
            [`${SECT_COMPANY}-postal`]: licCompanyAddr.postalCode || licCompanyAddr.postalcode || '',
            [`${SECT_COMPANY}-block-house`]: licCompanyAddr.blockNo || licCompanyAddr.blockno || '',
            [`${SECT_COMPANY}-street`]: licCompanyAddr.streetName || licCompanyAddr.streetname || '',
            [`${SECT_COMPANY}-floor`]: licCompanyAddr.floor || '',
            [`${SECT_COMPANY}-unit`]: licCompanyAddr.unit || '',
            [`${SECT_COMPANY}-building`]: licCompanyAddr.buildingName || licCompanyAddr.buildingname || '',
            // Address for Singpass
            [`${SECT_ADDRESS}-postal`]: applicantAddress.postalCode || licCompanyAddr.postalcode,
            [`${SECT_ADDRESS}-block-house`]: applicantAddress.blockNo || licCompanyAddr.blockno,
            [`${SECT_ADDRESS}-street`]: applicantAddress.streetName || licCompanyAddr.streetname,
            [`${SECT_ADDRESS}-floor`]: applicantAddress.floor || licCompanyAddr.floor,
            [`${SECT_ADDRESS}-unit`]: applicantAddress.unit || licCompanyAddr.unit,
            [`${SECT_ADDRESS}-building`]: applicantAddress.buildingName || licCompanyAddr.buildingname
        }
    };

    
    // Handle cancel mode early - return only general and cancellation forms
    if (isCancelMode) {
        const licenceNode = licApp.licence || {};
        const cancellationInfo = licenceNode?.cancellationInformation || {};
        const cancellationForm: FormData = {
            id: 'cancellation-details',
            formID: 'cancellation-details',
            title: 'Cancellation Details',
            hasError: true,
            values: {
                'cancellation-details-application-number': content?.applicationNumber || content?.pyID || '',
                'cancellation-details-uen-no': licApplicant?.companyUEN || licCompany?.uen || '',
                'cancellation-details-licence-no': content?.licenceNumber || '',
                'cancellation-details-effective-date': cancellationInfo.effectiveDateOfCancellation || '',
                'cancellation-details-cancellation-reason': cancellationInfo.cancellationReason || '',
                'cancellation-details-others-reason': cancellationInfo.ifOthersPleaseStateReason || '',
                'cancellation-details-remarks': cancellationInfo.Remarks || ''
            }
        };
        
        return [generalForm, cancellationForm];
    }

    // Add placeholder forms so wizard shows all ROE sections defined in INDUSTRIES (contact & application details)
    // Contact & Billing details population (previously placeholder). Map from nested licence nodes if present.
    const contactPerson = licApp.licence?.contactPersonParticulars || {};
    const contactAddress = licApp.licence?.contactPersonAddress || {};
    const billingPerson = licApp.licence?.billingPersonParticulars || {};
    const billingAddress = licApp.licence?.billingAddress || {};
    const contactAddr = contactAddress.address || {};
    const billingAddr = billingAddress.address || {};
    // Helper to normalise backend same-as flags: only return 'Yes' if explicitly 'Yes' or true; else '' so UI shows unselected.
    // Reverted: retain earlier behavior. If backend sends 'Yes'/true -> 'Yes', if 'No'/false -> 'No', else empty string.
    const yesIf = (v: any) => (v === 'Yes' || v === true ? 'Yes' : v === 'No' || v === false ? 'No' : '');
    const sameAsApplicantVal = yesIf(contactPerson.sameAsApplicantParticulars);
    const sameAsCompanyAddrVal = yesIf(contactAddress.sameAsCompanyRegisteredAddress);
    const sameAsContactParticularsVal = yesIf(billingPerson.sameAsContactPerson || billingPerson.sameAsContactPersonParticulars);
    const sameAsContactAddressVal = yesIf(billingAddress.sameAsContactPersonAddress || billingAddress.sameAsMailingAddress);
    const contactBillingForm: FormData = {
        id: `contact-billing-details${formSuffix}`,
        formID: `contact-billing-details${formSuffix}`,
        title: 'Billing Details',
        hasError: false, // Start as valid, will be checked by validation logic
        values: {
            // Section: contact-particulars (sectionID = 'contact-particulars')
            'contact-particulars-same-as-applicant': sameAsApplicantVal,
            'contact-particulars-contactName': contactPerson.name,
            'contact-particulars-designation': contactPerson.designation,
            'contact-particulars-telNumber': contactPerson.officeTelNumber,
            'contact-particulars-mobileNo': contactPerson.mobileNumber,
            'contact-particulars-email': contactPerson.email,
            'contact-particulars-faxNo': contactPerson.faxNumber,
            // Canonical keys expected: `${sectionID}-${fieldId}` e.g. 'contact-particulars-same-as-applicant'
            // Provide raw fallback keys (without section prefix) to support renderer fallback logic
            'same-as-applicant': sameAsApplicantVal,
            'contactName': contactPerson.name,
            'designation': contactPerson.designation,
            'telNumber': contactPerson.officeTelNumber,
            'mobileNo': contactPerson.mobileNumber,
            'email': contactPerson.email,
            'faxNo': contactPerson.faxNumber,
            // Section: contact-address
            'contact-address-same-as-company-address': sameAsCompanyAddrVal,
            'contact-address-postal': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.postalCode || '' : '',
            'contact-address-blockHouse': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.blockNo || '' : '',
            'contact-address-street': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.streetName || '' : '',
            'contact-address-floor': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.floor || '' : '',
            'contact-address-unit': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.unit || '' : '',
            'contact-address-building': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.buildingName || '' : '',
            'same-as-company-address': sameAsCompanyAddrVal,
            // Section: billing-particulars
            'billing-particulars-same-as-contact': sameAsContactParticularsVal,
            'billing-particulars-billingName': billingPerson.name,
            'billing-particulars-designation': billingPerson.designation,
            'billing-particulars-telNumber': billingPerson.officeTelNumber,
            'billing-particulars-mobileNo': billingPerson.mobileNumber,
            'billing-particulars-email': billingPerson.email,
            'billing-particulars-faxNo': billingPerson.faxNumber,
            'same-as-contact': sameAsContactParticularsVal,
            'billingName': billingPerson.name,
            // reuse designation/telNumber/mobileNo/email raw keys already set above if overlapping naming
            // Section: billing-address
            'billing-address-same-as-contact-address': sameAsContactAddressVal,
            'billing-address-postal': sameAsContactAddressVal !== 'Yes' ? billingAddr.postalCode || '' : '',
            'billing-address-blockHouse': sameAsContactAddressVal !== 'Yes' ? billingAddr.blockNo || '' : '',
            'billing-address-street': sameAsContactAddressVal !== 'Yes' ? billingAddr.streetName || '' : '',
            'billing-address-floor': sameAsContactAddressVal !== 'Yes' ? billingAddr.floor || '' : '',
            'billing-address-unit': sameAsContactAddressVal !== 'Yes' ? billingAddr.unit || '' : '',
            'billing-address-building': sameAsContactAddressVal !== 'Yes' ? billingAddr.buildingName || '' : '',
            'same-as-contact-address': sameAsContactAddressVal
        }
    };
    // --- Application Details (simplified for ACA) ---
    // Backend contract for ACA focuses on licence structure rather than exam details
    const licenceNode = licApp.licence || {};
    
    // Fetch uploaded attachments from applicationattachment API
    // Only for draft editing (when caseTypeID exists and transactionType is not set yet or is 'New')
    const isDraftEdit = caseTypeID && (!content?.transactionType || content?.transactionType === 'New');
    const uploadedAttachments = isDraftEdit ? await requestAttachmentListApplication(caseTypeID) : [];
    
    // Map attachments by category for easy lookup
    const attachmentsByCategory: Record<string, any[]> = {};
    if (Array.isArray(uploadedAttachments)) {
        uploadedAttachments.forEach((attachment: any) => {
            const category = attachment.pyCategory || '';
            if (!attachmentsByCategory[category]) {
                attachmentsByCategory[category] = [];
            }
            
            // FileUpload component has a bug at line 455: downloadLink.download = file.fileName.split('.')[0]
            // This strips the extension! We fixed it to check file.saved, but need correct fileName from API
            const fullFileName = attachment.fileName || '';
            
            // Parse extension properly - API might return filename without extension
            const fileNameParts = fullFileName.split('.');
            const hasExtension = fileNameParts.length > 1;
            const extension = hasExtension ? fileNameParts.pop() || 'pdf' : 'pdf';
            const nameWithoutExt = hasExtension ? fileNameParts.join('.') : fullFileName;
            
            // Ensure fileName always includes extension for download
            const fileNameWithExtension = hasExtension ? fullFileName : `${fullFileName}.pdf`;
            
            attachmentsByCategory[category].push({
                ...attachment, // Preserve ALL API properties exactly as returned
                ID: attachment.DownloadLink?.split('/')[2] || '',
                fileName: fileNameWithExtension, // Ensure extension is present
                name: nameWithoutExt,
                extension: extension,
                mimeType: attachment.mimeType || getMimeTypeFromExtension(fullFileName) || 'application/pdf',
                category: attachment.pyCategory || '',
                saved: true,
                deleted: false,
                uploading: false,
                error: false
            });
        });
    }
    // For amend view/edit, map iWantToUpdate to appropriate table (similar to EXBF pattern)
    // NOTE: Renew does NOT use iWantToUpdate - it shows all fields unconditionally
    const iWantToUpdateArray = (application === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND-VIEW' || isAmendMode) 
        ? (licenceNode?.licenceDetails?.iWantToUpdate || licenceNode?.iWantToUpdate || [])
        : [];
    
    const mapped = iWantToUpdateArray
        .filter((data: { pySelected: string }) => data.pySelected === 'true')
        .map((data: { mixedValue: string }) => ({
            type: data.mixedValue
        }));
    
    // Extract amendType from iWantToUpdate for edit mode
    let amendType: 'Material' | 'Non-Material' | 'Both' | null = null;
    if (isAmendMode && iWantToUpdateArray.length > 0) {
        const selectedTypes = iWantToUpdateArray
            .filter((data: { pySelected: string }) => data.pySelected === 'true')
            .map((data: { mixedValue: string }) => data.mixedValue);
        
        const hasMaterial = selectedTypes.includes('Material Fields');
        const hasNonMaterial = selectedTypes.includes('Non Material Fields');
        
        if (hasMaterial && hasNonMaterial) {
            amendType = 'Both';
        } else if (hasMaterial) {
            amendType = 'Material';
        } else if (hasNonMaterial) {
            amendType = 'Non-Material';
        }
    }
    
    // For ACA, we aggregate all application details into a single form
    // For VIEW mode: use fieldSuffix (empty) so keys match ACA_APPLICATION_FIELDS
    // For EDIT mode: use fieldSuffix (-amend) so keys match ACA_AMEND_APPLICATION_FIELDS
    const applicationDetailsValues = {
        ...(liceGeneral && {
            [`transaction-type`]: liceGeneral.transactionType,
        }),
        // Add amend table for amend view/edit mode - using licence-category section ID from AMEND_ACA_APPLICATION
        ...((application === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND-VIEW' || isAmendMode) && {
            'licence-category-amend-table': Array.isArray(mapped) && mapped.length > 0 ? [...mapped] : []
        }),
        // Expose backend case identifier for display
        'application-details-caseid': content.caseid || content.caseID || licApp.caseid || licApp.caseID || '',
        // Application number (preferred display identifier)
        'application-details-application-number': content.applicationNumber || licApp.applicationNumber || '',
        // Licence name (fallback to static if backend omits)
        'application-details-licence-name': content?.licenceName || 'Accreditation of Certification Authority',
        // Company details from licence.companyDetails
        ...(licenceNode.companyDetails && {
            [`${SECT_COMPANY_DETAILS}${fieldSuffix}-registered-as`]: licenceNode.companyDetails.companyRegisteredAs,
            [`${SECT_COMPANY_DETAILS}${fieldSuffix}-registered-as-others`]: licenceNode.companyDetails.ifOthersPleaseSpecify,
            [`${SECT_COMPANY_DETAILS}${fieldSuffix}-principal-business-activities`]: licenceNode.companyDetails.principalBusinessActivities,
            [`${SECT_COMPANY_DETAILS}${fieldSuffix}-company-website-url`]: licenceNode.companyDetails.companyURL
        }),
        // Company documents
        [`${SECT_COMPANY_DETAILS}${fieldSuffix}-company-documents-0-company-documents-upload`]: attachmentsByCategory['CompanyDocuments'] || licenceNode.companyDocuments || [],
        // Ownership details - ensure at least one row
        [`${SECT_OWNERSHIP}${fieldSuffix}-shareholder-list`]: licenceNode.ownership?.shareholderList?.length > 0 
            ? licenceNode.ownership.shareholderList.map((s: any) => ({
                name: s.shareHolderName,
                country: s.countryOfIncorporation,
                'share-percentage': s.sharePercent,
                'nature-of-business': s.businessNature
            }))
            : [{ name: '', country: '', 'share-percentage': '', 'nature-of-business': '' }], // Default single row
        // Shareholder documents
        [`${SECT_OWNERSHIP}${fieldSuffix}-shareholder-documents-0-shareholder-documents-upload`]: attachmentsByCategory['ShareholderDocuments'] || licenceNode.shareholderDocuments || [],
        // Financial statements - ensure at least one row
        [`${SECT_FINANCIAL_DETAILS}${fieldSuffix}-statement-list`]: licenceNode.financialStatementsForPastYearsList?.length > 0
            ? licenceNode.financialStatementsForPastYearsList.map((f: any) => ({
                year: f.financialYearEnd,
                revenue: f.financialrevenue,
                'profits-before-tax': f.profitsBeforeTax,
                'profits-after-tax': f.profitsAfterTax,
                'total-assets': f.totalAssets,
                'total-liabilities': f.totalLiabilities
            }))
            : [{ 
                year: '', 
                revenue: '', 
                'profits-before-tax': '', 
                'profits-after-tax': '', 
                'total-assets': '', 
                'total-liabilities': '' 
            }], // Default single row
        // Auditors - populated via modal, no default rows
        [`${SECT_FINANCIAL_DETAILS}${fieldSuffix}-auditors-list`]: licenceNode.auditInfo?.auditorList?.length > 0
            ? licenceNode.auditInfo.auditorList.map((a: any) => ({
                name: a.auditorName,
                postal: a.address?.postalCode,
                building: a.address?.buildingName,
                'block-house': a.address?.blockNo,
                street: a.address?.streetName,
                floor: a.address?.floor,
                unit: a.address?.unit
            }))
            : [], // No default rows - added via modal
        // Auditor documents
        [`${SECT_FINANCIAL_DETAILS}${fieldSuffix}-auditor-documents-0-auditor-documents-upload`]: attachmentsByCategory['AuditorDocuments'] || licenceNode.auditorDocuments || [],
        // Funding credit - ensure at least one row
        [`${SECT_FUNDING_CREDIT_INFORMATION}${fieldSuffix}-funding-credit-information-list`]: licenceNode.fundingCreditList?.length > 0
            ? licenceNode.fundingCreditList.map((f: any) => ({
                'source-of-funding': f.sourceOfFunding,
                amount: f.fundAmount,
                'details-of-any-collateral': f.collateralDetails,
                'name-of-contact-person-for-verification': f.contactPersonForVerification,
                'contact-number': f.contactNo,
                'email-address': f.fundingCreditEmail
            }))
            : [{ 
                'source-of-funding': '', 
                amount: '', 
                'details-of-any-collateral': '', 
                'name-of-contact-person-for-verification': '', 
                'contact-number': '', 
                'email-address': '' 
            }], // Default single row
        // Business Plans documents
        [`${SECT_BUSINESS_PLANS}${fieldSuffix}-business-plans-documents-0-business-plans-documents-upload`]: attachmentsByCategory['BusinessPlansDocuments'] || licenceNode.businessPlansDocuments || [],
        // Personnel documents
        [`${SECT_PERSONNEL}${fieldSuffix}-personnel-documents-0-personnel-documents-upload`]: attachmentsByCategory['PersonnelDocuments'] || licenceNode.personnelDocuments || [],
        // Policies and Operations documents
        [`${SECT_POLICIES_OPERATIONS}${fieldSuffix}-policies-and-operations-documents-0-policies-and-operations-documents-upload`]: attachmentsByCategory['PoliciesOperationsDocuments'] || licenceNode.policiesOperationsDocuments || [],
        // Insolvency info - normalize backend boolean/string values to 'Yes'/'No'
        ...(licenceNode.InsolvencyLegalProceedingsInfo && {
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-substantial-shareholder`]: licenceNode.InsolvencyLegalProceedingsInfo.isBeingWoundUpOrLiquidated === true || licenceNode.InsolvencyLegalProceedingsInfo.isBeingWoundUpOrLiquidated === 'true' ? 'Yes' : licenceNode.InsolvencyLegalProceedingsInfo.isBeingWoundUpOrLiquidated === false || licenceNode.InsolvencyLegalProceedingsInfo.isBeingWoundUpOrLiquidated === 'false' ? 'No' : licenceNode.InsolvencyLegalProceedingsInfo.isBeingWoundUpOrLiquidated,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-substantial-shareholder-details`]: licenceNode.InsolvencyLegalProceedingsInfo.beingWoundUpOrLiquidatedDetails,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-involved-legal`]: licenceNode.InsolvencyLegalProceedingsInfo.isInvolvedInLegalProceedingAsCA === true || licenceNode.InsolvencyLegalProceedingsInfo.isInvolvedInLegalProceedingAsCA === 'true' ? 'Yes' : licenceNode.InsolvencyLegalProceedingsInfo.isInvolvedInLegalProceedingAsCA === false || licenceNode.InsolvencyLegalProceedingsInfo.isInvolvedInLegalProceedingAsCA === 'false' ? 'No' : licenceNode.InsolvencyLegalProceedingsInfo.isInvolvedInLegalProceedingAsCA,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-involved-legal-details`]: licenceNode.InsolvencyLegalProceedingsInfo.involvedInLegalProceedingAsCADetails || licenceNode.InsolvencyLegalProceedingsInfo.invovledInLegalProceedingAsCADetails,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-compromise-creditors`]: licenceNode.InsolvencyLegalProceedingsInfo.isEnteredIntoAnyCompromiseWithCreditors === true || licenceNode.InsolvencyLegalProceedingsInfo.isEnteredIntoAnyCompromiseWithCreditors === 'true' ? 'Yes' : licenceNode.InsolvencyLegalProceedingsInfo.isEnteredIntoAnyCompromiseWithCreditors === false || licenceNode.InsolvencyLegalProceedingsInfo.isEnteredIntoAnyCompromiseWithCreditors === 'false' ? 'No' : licenceNode.InsolvencyLegalProceedingsInfo.isEnteredIntoAnyCompromiseWithCreditors,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-compromise-creditors-details`]: licenceNode.InsolvencyLegalProceedingsInfo.enteredIntoAnyCompromiseWithCreditorsDetails,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-receiver-manager-shareholder`]: licenceNode.InsolvencyLegalProceedingsInfo.isReceiverAppointedToApplicantCompany === true || licenceNode.InsolvencyLegalProceedingsInfo.isReceiverAppointedToApplicantCompany === 'true' ? 'Yes' : licenceNode.InsolvencyLegalProceedingsInfo.isReceiverAppointedToApplicantCompany === false || licenceNode.InsolvencyLegalProceedingsInfo.isReceiverAppointedToApplicantCompany === 'false' ? 'No' : licenceNode.InsolvencyLegalProceedingsInfo.isReceiverAppointedToApplicantCompany,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-receiver-manager-shareholder-details`]: licenceNode.InsolvencyLegalProceedingsInfo.receiverAppointedToApplicantCompanyDetails || licenceNode.InsolvencyLegalProceedingsInfo.receiverAppointedtoApplicantCompanyDetails,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-fraudulent-dishonest`]: licenceNode.InsolvencyLegalProceedingsInfo.isConvictedOfAnOffence === true || licenceNode.InsolvencyLegalProceedingsInfo.isConvictedOfAnOffence === 'true' ? 'Yes' : licenceNode.InsolvencyLegalProceedingsInfo.isConvictedOfAnOffence === false || licenceNode.InsolvencyLegalProceedingsInfo.isConvictedOfAnOffence === 'false' ? 'No' : licenceNode.InsolvencyLegalProceedingsInfo.isConvictedOfAnOffence,
            [`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-fraudulent-dishonest-details`]: licenceNode.InsolvencyLegalProceedingsInfo.convictedOfAnOffenceDetails
        })
    };

    const applicationDetailsForm: FormData = {
        id: `application-details${formSuffix}`,
        formID: `application-details${formSuffix}`,
        title: 'Application Details',
        hasError: false, // Start as valid, will be checked by validation logic
        isRequiredIncomplete: false, // Will be calculated in context
        values: applicationDetailsValues
    };

    // Check if this is view mode (when we should show summary)
    // Exclude amend mode - amend should not show summary during creation
    const isViewMode = (application === 'IMDA-IRIS-Work-LICApplication-ACA-VIEW' || 
                       application === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND-VIEW' ||
                       ((content?.caseID || content?.caseid || content?.id) && !action)) &&
                       application !== 'IMDA-IRIS-Work-LICApplication-ACA-AMEND';
    
    if (isViewMode) {
        const summaryForm = {
            id: 'summary',
            formID: 'summary',
            title: 'Application Summary',
            hasError: false,
            values: {
                'summary-transactionType': content?.transactionType || content?.Type || 'New',
                'summary-application-name': content?.licenceName || 'Accreditation of Certification Authority',
                'summary-application-number': content?.applicationNumber || content?.pyID || '',
                'summary-application-businessStatus': content?.CustomerStatus || content?.BusinessStatus || 'In Progress',
                'summary-application-submission': formatDateTime(content?.pxCreateDateTime || content?.ApplicationDate || '', true)
            }
        };
        
        
        return { forms: [summaryForm, generalForm, contactBillingForm, applicationDetailsForm], amendType };
    }

    // For creation mode (ACA), no summary form
    return { forms: [generalForm, contactBillingForm, applicationDetailsForm], amendType };
};

// Removed generic applyGeneralMapping for ROE to avoid reintroducing legacy root nodes; explicit construction used instead.

const formatSave = async (
    details: DetailsType, 
    _caseTypeID: string, 
    pageInstructions: any[] = [], 
    _userRole?: string, 
    action?: string,
    amendTypeFromRedux?: 'Material' | 'Non-Material' | 'Both' | null
) => {
    if (action === 'application-delete') return payloadDeleteApplication;
    if (action === 'application-withdraw') {
        const withdrawForm = details.forms.find((form: FormData) => form.id === 'application-withdraw')?.values || {};
        const withdrawalReason = String(withdrawForm['aecWithdraw-reason'] || '');
        return payloadWithdrawApplication(withdrawalReason, 'ALT2');
    }
    
    // Detect if this is a cancel case by checking if cancellation-details form exists
    const isCancelMode = details.forms.some((form: FormData) => form.id === 'cancellation-details');
    
    // Handle cancel mode - create cancellation payload
    if (isCancelMode) {
        const general = details.forms.find((form: FormData) => form.id === 'general-cancel')?.values || {};
        const cancellationDetails = details.forms.find((form: FormData) => form.id === 'cancellation-details')?.values || {};
        const appliedAsValue = general[`${SECT_TYPE}-applicant-type`];
        
        const cancelContent: any = {
            licenceName: 'Accreditation of Certification Authority',
            transactionType: 'Cancel',
            AppSource: 'FEP',
            Type: 'Individual',
            pyWorkIDPrefix: 'LI-',
            // Include licence number from cancellation details form (read-only field)
            licenceNumber: cancellationDetails['cancellation-details-licence-no'] || '',
            LICApplication: {
                applicant: {
                    idType: general[`${SECT_APPLICANT}-id-type`],
                    companyUEN: general[`${SECT_APPLICANT}-uen-no`] || general[`${SECT_COMPANY}-uen-no`],
                    companyName: general[`${SECT_APPLICANT}-company`] || general[`${SECT_COMPANY}-company`],
                    ...(general[`${SECT_ADDRESS}-postal`] && {
                        address: {
                            buildingName: general[`${SECT_ADDRESS}-building`],
                            streetName: general[`${SECT_ADDRESS}-street`],
                            unit: general[`${SECT_ADDRESS}-unit`],
                            blockNo: general[`${SECT_ADDRESS}-block-house`],
                            postalCode: general[`${SECT_ADDRESS}-postal`],
                            floor: general[`${SECT_ADDRESS}-floor`]
                        }
                    }),
                    contactNumber: general[`${SECT_APPLICANT}-mobile-no-main`],
                    name: general[`${SECT_APPLICANT}-name`],
                    salutation: general[`${SECT_APPLICANT}-salutaion`],
                    idNumber: general[`${SECT_APPLICANT}-idno`],
                    email: general[`${SECT_APPLICANT}-email`]
                },
                filer: appliedAsValue !== 'Applicant' ? {
                    idType: general[`${SECT_FILER}-id-type`],
                    idNumber: general[`${SECT_FILER}-idno`],
                    email: general[`${SECT_FILER}-email`],
                    name: general[`${SECT_FILER}-name`],
                    salutation: general[`${SECT_FILER}-salutaion`],
                    contactNumber: general[`${SECT_FILER}-mobile-no-main`]
                } : undefined,
                company: {
                    uen: general[`${SECT_COMPANY}-uen-no`] || general[`${SECT_APPLICANT}-uen-no`],
                    companyName: general[`${SECT_COMPANY}-company`] || general[`${SECT_APPLICANT}-company`],
                    entityType: general[`${SECT_COMPANY}-entity-type`],
                    registeredAddress: {
                        buildingName: general[`${SECT_COMPANY}-building`],
                        streetName: general[`${SECT_COMPANY}-street`],
                        unit: general[`${SECT_COMPANY}-unit`],
                        blockno: general[`${SECT_COMPANY}-block-house`],
                        postalcode: general[`${SECT_COMPANY}-postal`],
                        floor: general[`${SECT_COMPANY}-floor`]
                    }
                },
                licence: {
                    cancellationInformation: {
                        effectiveDateOfCancellation: formatCancellationDate(cancellationDetails['cancellation-details-effective-date']),
                        cancellationReason: cancellationDetails['cancellation-details-cancellation-reason'] || '',
                        ifOthersPleaseStateReason: cancellationDetails['cancellation-details-others-reason'] || '',
                        Remarks: cancellationDetails['cancellation-details-remarks'] || ''
                    }
                },
                profile: { applyAs: appliedAsValue === 'Applicant' ? 'Applicant' : 'On behalf of applicant' }
            }
        };
        
        // Remove filer if not needed
        if (appliedAsValue === 'Applicant') {
            delete cancelContent.LICApplication.filer;
        }
        
        return {
            caseTypeID: 'IMDA-IRIS-Work-LICApplication',
            content: cancelContent
        };
    }
    
    // Detect transaction mode by checking form suffixes
    const isAmendMode = details.forms.some((form: FormData) => 
        form.id?.includes('-amend') || form.formID?.includes('-amend')
    );
    
    const isRenewMode = details.forms.some((form: FormData) => 
        form.id?.includes('-renew') || form.formID?.includes('-renew')
    );
    
    // Field suffix: Only amend mode uses -amend suffix on field section IDs
    // Renew and new modes use base section IDs (no suffix)
    const fieldSuffix = isAmendMode ? '-amend' : '';
    
    // Form suffix: Used to find the correct form by ID
    // For amend: -amend
    // For renew: -renew or -renew-nopay (check which one exists)
    let formSuffix = '';
    if (isAmendMode) {
        formSuffix = '-amend';
    } else if (isRenewMode) {
        // Check if it's -renew-nopay or -renew
        const hasNoPaySuffix = details.forms.some((form: FormData) => 
            form.id?.includes('-renew-nopay') || form.formID?.includes('-renew-nopay')
        );
        formSuffix = hasNoPaySuffix ? '-renew-nopay' : '-renew';
    }
    
    // Use amendType from Redux state (passed as parameter)
    const amendType = amendTypeFromRedux;
    
    const general = details.forms.find((form: FormData) => form.id === `general${formSuffix}`)?.values || {};
    const contactBilling = details.forms.find((form: FormData) => form.id === `contact-billing-details${formSuffix}`)?.values || {};
    const appliedAsValue = general[`${SECT_TYPE}-applicant-type`];
    // (Removed unused generalIDno / generalBDay in revised contract)

    // ROE specific: no EventList processing, so no per-event page instructions.
    let pageInstructionsForDelete: any[] = [];

    // Determine transaction type based on detected mode
    let transactionType = 'New';
    if (isAmendMode) {
        transactionType = 'Amend';
    } else if (isRenewMode) {
        transactionType = 'Renew';
    }
    
    const hasPaymentStep = transactionType === 'New' || (transactionType === 'Renew' && formSuffix !== '-renew-nopay');

    const baseContent: any = {
        licenceName: 'Accreditation of Certification Authority',
        transactionType,
        AppSource: 'FEP',
        Type: 'Individual',
        ...(isAmendMode && { pyWorkIDPrefix: 'LI-' }),
        LICApplication: {
            applicant: {},
            filer: {},
            company: { registeredAddress: {} },
            licence: (isAmendMode || isRenewMode) ? {
                licenceDetails: isAmendMode ? {
                    iWantToUpdate: [
                        ...(amendType === 'Material' || amendType === 'Both' ? [{
                            mixedValue: 'Material Fields',
                            pySelected: 'true'
                        }] : []),
                        ...(amendType === 'Non-Material' || amendType === 'Both' ? [{
                            mixedValue: 'Non Material Fields',
                            pySelected: 'true'
                        }] : [])
                    ]
                } : {}
            } : {},
            payment: {
                IsFEPAppFeeApplicable: hasPaymentStep ? "true" : "false"
            },
            profile: { applyAs: appliedAsValue === 'Applicant' ? 'Applicant' : 'On behalf of applicant' }
        }
    };

    // Map contact & billing details to licence node
    const contactPersonParticulars: any = {};
    const contactPersonAddress: any = { address: {} };
    const billingPersonParticulars: any = {};
    const billingPersonAddress: any = { address: {} };

    // Contact Person Particulars
    if (contactBilling['contact-particulars-contactName'] || contactBilling['contactName']) {
        contactPersonParticulars.name = contactBilling['contact-particulars-contactName'] || contactBilling['contactName'];
    }
    if (contactBilling['contact-particulars-designation'] || contactBilling['designation']) {
        contactPersonParticulars.designation = contactBilling['contact-particulars-designation'] || contactBilling['designation'];
    }
    if (contactBilling['contact-particulars-telNumber'] || contactBilling['telNumber']) {
        contactPersonParticulars.officeTelNumber = contactBilling['contact-particulars-telNumber'] || contactBilling['telNumber'];
    }
    if (contactBilling['contact-particulars-mobileNo'] || contactBilling['mobileNo']) {
        contactPersonParticulars.mobileNumber = contactBilling['contact-particulars-mobileNo'] || contactBilling['mobileNo'];
    }
    if (contactBilling['contact-particulars-email'] || contactBilling['email']) {
        contactPersonParticulars.email = contactBilling['contact-particulars-email'] || contactBilling['email'];
    }
    if (contactBilling['contact-particulars-faxNo'] || contactBilling['faxNo']) {
        contactPersonParticulars.faxNumber = contactBilling['contact-particulars-faxNo'] || contactBilling['faxNo'];
    }
    if (contactBilling['contact-particulars-same-as-applicant'] || contactBilling['same-as-applicant']) {
        contactPersonParticulars.sameAsApplicantParticulars = contactBilling['contact-particulars-same-as-applicant'] || contactBilling['same-as-applicant'];
    }

    // Contact Address - only send if NOT "same as"
    const sameAsCompanyAddr = contactBilling['contact-address-same-as-company-address'] || contactBilling['same-as-company-address'];
    
    // When "Same As" = Yes, copy from company registered address
    if (sameAsCompanyAddr === 'Yes') {
        contactPersonAddress.address.postalCode = general[`${SECT_COMPANY}-postal`] || '';
        contactPersonAddress.address.blockNo = general[`${SECT_COMPANY}-block-house`] || '';
        contactPersonAddress.address.streetName = general[`${SECT_COMPANY}-street`] || '';
        contactPersonAddress.address.floor = general[`${SECT_COMPANY}-floor`] || '';
        contactPersonAddress.address.unit = general[`${SECT_COMPANY}-unit`] || '';
        contactPersonAddress.address.buildingName = general[`${SECT_COMPANY}-building`] || '';
    } else {
        // When "Same As" = No, use contact address fields
        if (contactBilling['contact-address-postal'] || contactBilling['postal']) {
            contactPersonAddress.address.postalCode = contactBilling['contact-address-postal'] || contactBilling['postal'];
        }
        if (contactBilling['contact-address-blockHouse'] || contactBilling['blockHouse']) {
            contactPersonAddress.address.blockNo = contactBilling['contact-address-blockHouse'] || contactBilling['blockHouse'];
        }
        if (contactBilling['contact-address-street'] || contactBilling['street']) {
            contactPersonAddress.address.streetName = contactBilling['contact-address-street'] || contactBilling['street'];
        }
        if (contactBilling['contact-address-floor'] || contactBilling['floor']) {
            contactPersonAddress.address.floor = contactBilling['contact-address-floor'] || contactBilling['floor'];
        }
        if (contactBilling['contact-address-unit'] || contactBilling['unit']) {
            contactPersonAddress.address.unit = contactBilling['contact-address-unit'] || contactBilling['unit'];
        }
        if (contactBilling['contact-address-building'] || contactBilling['building']) {
            contactPersonAddress.address.buildingName = contactBilling['contact-address-building'] || contactBilling['building'];
        }
    }
    if (sameAsCompanyAddr) {
        contactPersonAddress.sameAsCompanyRegisteredAddress = sameAsCompanyAddr;
    }

    // Billing Person Particulars
    if (contactBilling['billing-particulars-billingName'] || contactBilling['billingName']) {
        billingPersonParticulars.name = contactBilling['billing-particulars-billingName'] || contactBilling['billingName'];
    }
    if (contactBilling['billing-particulars-designation']) {
        billingPersonParticulars.designation = contactBilling['billing-particulars-designation'];
    }
    if (contactBilling['billing-particulars-telNumber']) {
        billingPersonParticulars.officeTelNumber = contactBilling['billing-particulars-telNumber'];
    }
    if (contactBilling['billing-particulars-mobileNo']) {
        billingPersonParticulars.mobileNumber = contactBilling['billing-particulars-mobileNo'];
    }
    if (contactBilling['billing-particulars-email']) {
        billingPersonParticulars.email = contactBilling['billing-particulars-email'];
    }
    if (contactBilling['billing-particulars-faxNo']) {
        billingPersonParticulars.faxNumber = contactBilling['billing-particulars-faxNo'];
    }
    if (contactBilling['billing-particulars-same-as-contact'] || contactBilling['same-as-contact']) {
        billingPersonParticulars.sameAsContactPersonParticulars = contactBilling['billing-particulars-same-as-contact'] || contactBilling['same-as-contact'];
    }

    // Billing Address - only send if NOT "same as"
    const sameAsContactAddr = contactBilling['billing-address-same-as-contact-address'] || contactBilling['same-as-contact-address'];
    
    // When "Same As" = Yes, copy from contact address
    if (sameAsContactAddr === 'Yes') {
        billingPersonAddress.address.postalCode = contactBilling['contact-address-postal'] || contactBilling['postal'] || '';
        billingPersonAddress.address.blockNo = contactBilling['contact-address-blockHouse'] || contactBilling['blockHouse'] || '';
        billingPersonAddress.address.streetName = contactBilling['contact-address-street'] || contactBilling['street'] || '';
        billingPersonAddress.address.floor = contactBilling['contact-address-floor'] || contactBilling['floor'] || '';
        billingPersonAddress.address.unit = contactBilling['contact-address-unit'] || contactBilling['unit'] || '';
        billingPersonAddress.address.buildingName = contactBilling['contact-address-building'] || contactBilling['building'] || '';
    } else {
        // When "Same As" = No, use billing address fields
        if (contactBilling['billing-address-postal']) {
            billingPersonAddress.address.postalCode = contactBilling['billing-address-postal'];
        }
        if (contactBilling['billing-address-blockHouse']) {
            billingPersonAddress.address.blockNo = contactBilling['billing-address-blockHouse'];
        }
        if (contactBilling['billing-address-street']) {
            billingPersonAddress.address.streetName = contactBilling['billing-address-street'];
        }
        if (contactBilling['billing-address-floor']) {
            billingPersonAddress.address.floor = contactBilling['billing-address-floor'];
        }
        if (contactBilling['billing-address-unit']) {
            billingPersonAddress.address.unit = contactBilling['billing-address-unit'];
        }
        if (contactBilling['billing-address-building']) {
            billingPersonAddress.address.buildingName = contactBilling['billing-address-building'];
        }
    }
    if (sameAsContactAddr) {
        billingPersonAddress.sameAsContactPersonAddress = sameAsContactAddr;
    }

    // Add contact & billing to licence node
    if (Object.keys(contactPersonParticulars).length > 0) {
        baseContent.LICApplication.licence.contactPersonParticulars = contactPersonParticulars;
    }
    if (Object.keys(contactPersonAddress.address).length > 0 || contactPersonAddress.sameAsCompanyRegisteredAddress) {
        baseContent.LICApplication.licence.contactPersonAddress = contactPersonAddress;
    }
    if (Object.keys(billingPersonParticulars).length > 0) {
        baseContent.LICApplication.licence.billingPersonParticulars = billingPersonParticulars;
    }
    if (Object.keys(billingPersonAddress.address).length > 0 || billingPersonAddress.sameAsContactPersonAddress) {
        baseContent.LICApplication.licence.billingAddress = billingPersonAddress;
    }

    // Map ACA application details to temporary licence schema structure
    const applicationDetails = details.forms.find((form: FormData) => form.id === `application-details${formSuffix}`)?.values || {};
    
    // Extract form values for mapping - use fieldSuffix for field keys
    const getFormValue = (key: string) => applicationDetails[key] || '';
    const getTableValue = (key: string) => {
        const value = applicationDetails[key];
        return Array.isArray(value) ? value : [];
    };
    
    // Build licence.companyDetails node
    const companyDetails: any = {};
    if (getFormValue(`${SECT_COMPANY_DETAILS}${fieldSuffix}-registered-as`)) {
        companyDetails.companyRegisteredAs = getFormValue(`${SECT_COMPANY_DETAILS}${fieldSuffix}-registered-as`);
    }
    if (getFormValue(`${SECT_COMPANY_DETAILS}${fieldSuffix}-registered-as-others`)) {
        companyDetails.ifOthersPleaseSpecify = getFormValue(`${SECT_COMPANY_DETAILS}${fieldSuffix}-registered-as-others`);
    }
    if (getFormValue(`${SECT_COMPANY_DETAILS}${fieldSuffix}-principal-business-activities`)) {
        companyDetails.principalBusinessActivities = getFormValue(`${SECT_COMPANY_DETAILS}${fieldSuffix}-principal-business-activities`);
    }
    if (getFormValue(`${SECT_COMPANY_DETAILS}${fieldSuffix}-company-website-url`)) {
        companyDetails.companyURL = getFormValue(`${SECT_COMPANY_DETAILS}${fieldSuffix}-company-website-url`);
    }
    
    // Build licence.ownership node
    const ownership: any = {};
    const shareholderList = (getTableValue(`${SECT_OWNERSHIP}${fieldSuffix}-shareholder-list`) as any[] || [])
        .filter((row: any) => row && (row.name || row['name']))
        .map((row: any) => ({
            shareHolderName: row.name || '',
            countryOfIncorporation: row.country || '',
            sharePercent: row['share-percentage'] || '',
            businessNature: row['nature-of-business'] || ''
        }));

    if (shareholderList.length > 0) {
        ownership.shareholderList = shareholderList;
    }
    
    // Build licence.financialStatementsForPastYearsList
    const financialStatements = (getTableValue(`${SECT_FINANCIAL_DETAILS}${fieldSuffix}-statement-list`) as any[] || [])
        .filter((row: any) => row && row.year)
        .map((row: any) => ({
            financialYearEnd: row.year || '',
            financialrevenue: row.revenue || '',
            profitsBeforeTax: row['profits-before-tax'] || '',
            profitsAfterTax: row['profits-after-tax'] || '',
            totalAssets: row['total-assets'] || '',
            totalLiabilities: row['total-liabilities'] || ''
        }));
    
    // Build licence.auditInfo
    const auditInfo: any = {};
    const auditorList = (getTableValue(`${SECT_FINANCIAL_DETAILS}${fieldSuffix}-auditors-list`) as any[] || [])
        .filter((row: any) => row && row.name)
        .map((row: any) => ({
            auditorName: row.name || '',
            address: {
                postalCode: row.postal || '',
                buildingName: row.building || '',
                blockNo: row['block-house'] || '',
                streetName: row.street || '',
                floor: row.floor || '',
                unit: row.unit || ''
            }
        }));
    if (auditorList.length > 0) {
        auditInfo.auditorList = auditorList;
    }
    
    // Build licence.fundingCreditList
    const fundingCreditList = (getTableValue(`${SECT_FUNDING_CREDIT_INFORMATION}${fieldSuffix}-funding-credit-information-list`) as any[] || [])
        .filter((row: any) => row && (row['source-of-funding'] || row.amount))
        .map((row: any) => ({
            sourceOfFunding: row['source-of-funding'] || '',
            fundAmount: row.amount || '',
            collateralDetails: row['details-of-any-collateral'] || '',
            contactPersonForVerification: row['name-of-contact-person-for-verification'] || '',
            contactNo: row['contact-number'] || '',
            fundingCreditEmail: row['email-address'] || ''
        }));
    
    // Build licence.InsolvencyLegalProceedingsInfo
    const insolvencyInfo: any = {};
    const insolvencyMappings = [
        { frontend: 'substantial-shareholder', backend: 'isBeingWoundUpOrLiquidated', detailBackend: 'beingWoundUpOrLiquidatedDetails' },
        { frontend: 'involved-legal', backend: 'isInvolvedInLegalProceedingAsCA', detailBackend: 'involvedInLegalProceedingAsCADetails' },
        { frontend: 'compromise-creditors', backend: 'isEnteredIntoAnyCompromiseWithCreditors', detailBackend: 'enteredIntoAnyCompromiseWithCreditorsDetails' },
        { frontend: 'receiver-manager-shareholder', backend: 'isReceiverAppointedToApplicantCompany', detailBackend: 'receiverAppointedToApplicantCompanyDetails' },
        { frontend: 'fraudulent-dishonest', backend: 'isConvictedOfAnOffence', detailBackend: 'convictedOfAnOffenceDetails' }
    ];
    
    insolvencyMappings.forEach(({ frontend, backend, detailBackend }) => {
        const answer = getFormValue(`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-${frontend}`);
        const details = getFormValue(`${SECT_INFORMATION_INSOLVENCY}${fieldSuffix}-${frontend}-details`);
        if (answer) {
            insolvencyInfo[backend] = answer;
            if (details && answer === 'Yes') {
                insolvencyInfo[detailBackend] = details;
            }
        }
    });
    
    // Note: File uploads are handled separately via requestUpload/requestAttach APIs
    // We don't include them in the save payload - they're managed independently
    // The attachment API is only used to fetch and display existing files
    
    // Assemble licence node
    const licenceNode: any = {
        // Add transactionType to licence node
        transactionType
    };
    if (Object.keys(companyDetails).length > 0) {
        licenceNode.companyDetails = companyDetails;
    }
    if (Object.keys(ownership).length > 0) {
        licenceNode.ownership = ownership;
    }
    if (financialStatements.length > 0) {
        licenceNode.financialStatementsForPastYearsList = financialStatements;
    }
    if (Object.keys(auditInfo).length > 0) {
        licenceNode.auditInfo = auditInfo;
    }
    if (fundingCreditList.length > 0) {
        licenceNode.fundingCreditList = fundingCreditList;
    }
    if (Object.keys(insolvencyInfo).length > 0) {
        licenceNode.InsolvencyLegalProceedingsInfo = insolvencyInfo;
    }
    
    // Add to base content
    if (Object.keys(licenceNode).length > 0) {
        baseContent.LICApplication.licence = { ...baseContent.LICApplication.licence, ...licenceNode };
    }
    

    // Build applicant & filer nodes per required contract structure (simple flattened address/contact)
    const applicantNode: any = {
        idType: general[`${SECT_APPLICANT}-id-type`],
        // In amend mode, don't include address as it's not editable/required
        // For renew mode, include address like new applications
        ...(!isAmendMode && {
            address: {
                buildingName: general[`${SECT_ADDRESS}-building`],
                streetName: general[`${SECT_ADDRESS}-street`],
                unit: general[`${SECT_ADDRESS}-unit`],
                blockNo: general[`${SECT_ADDRESS}-block-house`],
                postalCode: general[`${SECT_ADDRESS}-postal`],
                floor: general[`${SECT_ADDRESS}-floor`]
            }
        }),
        contactNumber: general[`${SECT_APPLICANT}-mobile-no-main`],
        name: general[`${SECT_APPLICANT}-name`],
        salutation: general[`${SECT_APPLICANT}-salutaion`],
        idNumber: general[`${SECT_APPLICANT}-idno`],
        email: general[`${SECT_APPLICANT}-email`]
    };
    const filerNode: any = {
        idType: general[`${SECT_FILER}-id-type`],
        idNumber: general[`${SECT_FILER}-idno`],
        email: general[`${SECT_FILER}-email`],
        name: general[`${SECT_FILER}-name`],
        salutation: general[`${SECT_FILER}-salutaion`],
        contactNumber: general[`${SECT_FILER}-mobile-no-main`],
        address: {
            buildingName: '',
            streetName: '',
            unit: '',
            blockNo: '',
            postalCode: '',
            floor: ''
        }
    };

    baseContent.LICApplication.applicant = applicantNode;
    // Add transactionType to LICApplication level
    baseContent.LICApplication.transactionType = transactionType;
    
    if (appliedAsValue !== 'Applicant') {
        baseContent.LICApplication.filer = filerNode;
    } else {
        delete baseContent.LICApplication.filer;
    }

    // Determine if user is Singpass (no UEN) or Corppass (has UEN)
    // For Singpass: applicant.address will have data from SECT_ADDRESS
    // For Corppass: company.registeredAddress will have data from SECT_COMPANY
    const hasCompanyData = Boolean(general[`${SECT_COMPANY}-uen-no`] || general[`${SECT_COMPANY}-company`]);
    const isSingpass = !hasCompanyData;
    
    // Add IDNo and IDNoRef based on login type
    if (isSingpass) {
        baseContent.IDNo = general[`${SECT_APPLICANT}-idno`];
        baseContent.IDNoRef = general[`${SECT_APPLICANT}-idno`];
    } else {
        baseContent.IDNo = general[`${SECT_COMPANY}-uen-no`];
        baseContent.IDNoRef = general[`${SECT_COMPANY}-uen-no`];
    }
    
    // Company node mapping - only for Corppass users
    if (hasCompanyData) {
        baseContent.LICApplication.company = {
            registeredAddress: {
                buildingName: general[`${SECT_COMPANY}-building`],
                streetName: general[`${SECT_COMPANY}-street`],
                unit: general[`${SECT_COMPANY}-unit`],
                blockNo: general[`${SECT_COMPANY}-block-house`],
                postalCode: general[`${SECT_COMPANY}-postal`],
                floor: general[`${SECT_COMPANY}-floor`]
            },
            entityType: general[`${SECT_COMPANY}-entity-type`] || 'LC',
            companyName: general[`${SECT_COMPANY}-company`],
            uen: general[`${SECT_COMPANY}-uen-no`]
        };
    }

    // Pruning logic: remove structurally empty nodes (backend accepts creation without them)
    const licNode = baseContent.LICApplication.licence || {};
    const examDet = licNode.ExamDetails || {};
    const hasExamDetailsData = Object.keys(examDet).length > 0;
    const otherLicenceKeys = Object.keys(licNode).filter(k => k !== 'ExamDetails');
    if (!hasExamDetailsData && otherLicenceKeys.length === 0) {
        delete baseContent.LICApplication.licence;
    }
    const comp = baseContent.LICApplication.company;
    const compAddr = comp?.registeredAddress || {};
    const compFields = [comp?.companyName, comp?.uen, comp?.entityType];
    const compAddrValues = [compAddr.buildingname, compAddr.streetname, compAddr.unit, compAddr.blockno, compAddr.postalcode, compAddr.floor];
    if (compFields.every(v => !v) && compAddrValues.every(v => !v)) {
        delete baseContent.LICApplication.company;
    }
    const filer = baseContent.LICApplication.filer;
    if (filer) {
        const filerFields = [filer.idType, filer.idNumber, filer.name, filer.email, filer.contactNumber];
        if (filerFields.every(v => !v)) delete baseContent.LICApplication.filer;
    }

    // Basic derived sync logic: if same-as flags are set, copy values accordingly (frontend may already hide fields but ensure backend consistency)
    if (contactBilling['contact-particulars-same-as-applicant'] === 'Yes') {
        // Mirror applicant to contact person
        baseContent.LICApplication = baseContent.LICApplication || {};
        baseContent.LICApplication.licence = baseContent.LICApplication.licence || {};
        const licence = baseContent.LICApplication.licence;
        const applicant = baseContent.LICApplication.applicant || {};
        licence.contactPersonParticulars = {
            ...(licence.contactPersonParticulars || {}),
            sameAsApplicantParticulars: 'Yes',
            name: applicant.name,
            email: applicant.email,
            mobileNumber: applicant.contactNumber,
            officeTelNumber: applicant.OfficeNumber, // Use same contact number for office tel
            salutation: applicant.salutation
        };
    }
    if (contactBilling['billing-particulars-same-as-contact'] === 'Yes') {
        baseContent.LICApplication = baseContent.LICApplication || {};
        baseContent.LICApplication.licence = baseContent.LICApplication.licence || {};
        const licence = baseContent.LICApplication.licence;
        const contactP = licence.contactPersonParticulars || {};
        licence.billingPersonParticulars = {
            ...(licence.billingPersonParticulars || {}),
            sameAsContactPersonParticulars: 'Yes',
            name: contactP.name,
            email: contactP.email,
            mobileNumber: contactP.mobileNumber,
            officeTelNumber: contactP.officeTelNumber,
            salutation: contactP.salutation,
            designation: contactP.designation
        };
    }

    // IMPORTANT: Backend expects canonical LICApplication case type. We still detect ROE via
    // content.LICApplication.general.licenceName === 'Radio Operator Exam' and/or applicationType flag elsewhere.
    return {
        caseTypeID: 'IMDA-IRIS-Work-LICApplication',
        content: baseContent,
    // pageInstructionsForDelete is expected to be empty for ROE; condition retained for future extensibility.
    ...(pageInstructionsForDelete.length > 0 && { pageInstructions: pageInstructionsForDelete }),
        ...(pageInstructions?.length > 0 && { pageInstructions })
    };
};

// ROE does not use performer / event / venue sections; keep minimal emptySet if consumer expects property.
const emptySet = {};

const acaPayload = {
    formatRetrieve,
    formatSave,
    emptySet,
    autoFillData
};

export default acaPayload;
